import React, { useState, useEffect } from 'react';
import EditableCell from '../EditableCell';
import { toast } from 'react-toastify';
import { WIG_OPTIONS, DEFAULT_WIG_VALUES, getColorOptions, getHairTypeOptions } from '../../constants/wigOptions';
import { inventoryService } from '../../services/inventoryService';
import { apiService } from '../../services/apiService';
import QuickSumModal from '../Inventory/QuickSumModal';
import BulkWarehouseModal from '../Inventory/BulkWarehouseModal';
import '../../components/styles/InventoryPage.css';
import MultiSelect from '../Inventory/MultiSelect';


const InventoryPage = () => {
  const [allInventory, setAllInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // Reduced from 50 for faster initial load
  const [totalRecords, setTotalRecords] = useState(0);
  const [showPagination, setShowPagination] = useState(true);
  const [newInventoryItem, setNewInventoryItem] = useState({});
  const [activeYearTab, setActiveYearTab] = useState('all');
  const [showQuickSum, setShowQuickSum] = useState(false);
  const [showBulkWarehouseModal, setShowBulkWarehouseModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteIcon, setShowDeleteIcon] = useState(null);

  // "Other" option states for custom input fields
  const [showWigTypeOther, setShowWigTypeOther] = useState(false);
  const [wigTypeOther, setWigTypeOther] = useState('');
  const [showTopSizeOther, setShowTopSizeOther] = useState(false);
  const [topSizeOther, setTopSizeOther] = useState('');

  // Ref for barcode input field
  const barcodeInputRef = React.useRef(null);

  const [appliedFilters, setAppliedFilters] = useState({
    hairLength: [], // Changed from 'all'
    wigType: [],
    cutStyle: [],
    baseColor: [],
    inventoryType: [],
    netSize: [],
    hairType: [],
    netType: [],
    laceType: [],
    warehouse: [],
    topSize: []
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
    const [yearCounts, setYearCounts] = useState({
      all: 0,
      2024: 0,
      2025: 0,
      2026: 0,
    archive: 0
    });
    // Add separate state for API counts vs filtered counts
  const [apiCounts, setApiCounts] = useState({
    all: 0,
    available: 0,
    sold: 0,
    'pre-assembly': 0,
    other: 0
  });

  // Keep statusCounts for display only when needed
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    available: 0,
    sold: 0,
    'pre-assembly': 0,
    other: 0
  });

// Add this helper function to your component or utils
const createHairLengthMapping = () => {
  if (!WIG_OPTIONS?.hairLengths) {
    return { mapping: {}, reverseMapping: {}, maxIndex: 0 };
  }
  const lengths = WIG_OPTIONS.hairLengths.filter(length => length !== ''); // Remove empty string
  const mapping = {};
  const reverseMapping = {};
  
  lengths.forEach((length, index) => {
    mapping[index] = length;
    reverseMapping[length] = index;
  });
  
  return { mapping, reverseMapping, maxIndex: lengths.length - 1 };
};

const { mapping: hairLengthMapping, reverseMapping: hairLengthReverse, maxIndex } = createHairLengthMapping();

// Initialize with safe defaults first
const [pendingFilters, setPendingFilters] = useState({
  hairLength: [], // Safe initialization
  wigType: [],
  cutStyle: [],
  baseColor: [],
  inventoryType: [],
  netSize: [],
  hairType: [],
  netType: [],
  laceType: [],
  warehouse: [],
  topSize: []
});
  // Initial load - fetch both status and year counts in ONE API call
  useEffect(() => {
    fetchStatusCounts(true); // true = also update year counts
  }, []);

  // When activeYearTab changes (after initial load), fetch status counts only
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip first run - already handled by the initial load effect above
    }
    fetchStatusCounts(); // Don't update year counts on tab change
  }, [activeYearTab]);

  useEffect(() => {
    fetchAllInventoryData();
  }, [currentPage, pageSize, activeStatusTab, activeYearTab, appliedFilters]);

const getYearFromDate = (dateModified, dateAdded) => {
  // Check DateModified first (user-set year), then fall back to DateAdded (original year)
  const dateToUse = dateModified || dateAdded;

  if (!dateToUse) return 'Unknown';
  try {
    const year = new Date(dateToUse).getFullYear();
    return year.toString();
  } catch {
    return 'Unknown';
  }
};

  const handleYearTabChange = (year) => {
    setActiveYearTab(year);
    setCurrentPage(1); // Reset to first page
    setActiveStatusTab('all'); // Reset status when year changes
  };



const fetchAllInventoryData = async () => {
  setLoading(true);
  setError(null);

  try {
    const data = await inventoryService.getInventoryStatus(
      currentPage,
      pageSize,
      activeStatusTab,
      activeYearTab,
      appliedFilters
    );

    // Map items and ensure year is calculated from DateModified/DateAdded
    const combined = [
      ...(data.inventory2025 || []).map(item => {
        const calculatedYear = getYearFromDate(item.DateModified, item.DateAdded);
        const mappedItem = {
          ...item,
          source: '2025', // Always use '2025' for items from the 2025 table
          year: calculatedYear
        };

        return mappedItem;
      }),
      ...(data.inventoryMain || []).map(item => {
        const calculatedYear = getYearFromDate(item.DateModified, item.DateAdded);
        return {
          ...item,
          source: 'main', // Always use 'main' for items from the main table
          year: calculatedYear
        };
      })
    ];

    const totalFiltered = data.pagination.total2025 + data.pagination.totalMain;

    setAllInventory(combined);
    setFilteredInventory(combined);
    setTotalRecords(totalFiltered); // This should now be the filtered total
  } catch (err) {
    console.error('Error fetching inventory:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  
  const getSourceFromYear = (dateString, originalSource) => {
    const year = getYearFromDate(dateString);
    if (year === '2026') return '2026';
    return originalSource;
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.trim()) {
      setShowPagination(false); 
      setSearchLoading(true);
      
      try {
        // Pass year and status to search
        const searchData = await inventoryService.searchInventory(
          query, 
          activeStatusTab, 
          activeYearTab
        );
        
        if (searchData.success) {
          // Add year to search results, keep the source from API
          const resultsWithYear = (searchData.results || []).map(item => ({
            ...item,
            year: getYearFromDate(item.DateModified, item.DateAdded),
            // Keep source from API response - it's already correct ('2025' or 'main')
            source: item.source || '2025'
          }));
          setFilteredInventory(resultsWithYear);
        }
        
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setShowPagination(true);
      setCurrentPage(1);
      setFilteredInventory([]);
      fetchAllInventoryData();
    }
  };

  const handleDeleteItem = async (item) => {
    // const confirmed = window.confirm(`Are you sure you want to delete item: ${item.Wig_Bar_Code}?`);
    
    // if (confirmed) {
      try {
        await inventoryService.deleteInventoryItem(item.Wig_Bar_Code, item.source);
        
        toast.success(`Item ${item.Wig_Bar_Code} has been permanently deleted`);
        
        // Remove from local state
        setAllInventory(prev => prev.filter(inv => 
          !(inv.Wig_Bar_Code === item.Wig_Bar_Code && inv.source === item.source)
        ));
        setFilteredInventory(prev => prev.filter(inv => 
          !(inv.Wig_Bar_Code === item.Wig_Bar_Code && inv.source === item.source)
        ));
        
        // Refresh counts
        fetchStatusCounts();
        fetchYearCounts();
        
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item: ' + error.message);
      }
    // }
    
    setShowDeleteIcon(null);
    setItemToDelete(null);
  };

  const updateBarcode = async (item, newBarcode) => {
    try {
      const result = await inventoryService.updateBarcode(
        item.Wig_Bar_Code, 
        newBarcode, 
        item.OrderIDLink
      );

      toast.success(
        <div>
          <strong>Barcode Updated Successfully!</strong>
          <br />
          <small>{result.note}</small>
          <br />
          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            <strong>Updated Records:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
              {result.updatedTables.inventory2025 > 0 && <li>2025 Inventory: {result.updatedTables.inventory2025} record(s)</li>}
              {result.updatedTables.mainInventory > 0 && <li>Main Inventory: {result.updatedTables.mainInventory} record(s)</li>}
              {result.updatedTables.orderEntry > 0 && <li>Order Entry: {result.updatedTables.orderEntry} record(s)</li>}
              {result.updatedTables.roomTasks > 0 && <li>Room Tasks: {result.updatedTables.roomTasks} record(s)</li>}
            </ul>
          </div>
        </div>,
        { autoClose: 5000 }
      );
      
      fetchAllInventoryData();

    } catch (error) {
      console.error('Error updating barcode:', error);
      toast.error('Failed to update barcode: ' + error.message);
    }
  };

  const updateInventoryItem = async (item, field, newValue) => {
    try {
      await inventoryService.updateInventoryItem(item.Wig_Bar_Code, field, newValue, item.source);

      // Immediately update local state to show the change
      setAllInventory(prev => prev.map(inv =>
        inv.Wig_Bar_Code === item.Wig_Bar_Code && inv.source === item.source
          ? { ...inv, [field]: newValue }
          : inv
      ));

      setFilteredInventory(prev => prev.map(inv =>
        inv.Wig_Bar_Code === item.Wig_Bar_Code && inv.source === item.source
          ? { ...inv, [field]: newValue }
          : inv
      ));

      if (field === 'InvStatus') {
        fetchStatusCounts();
      }

    } catch (error) {
      console.error('Error updating inventory:', error);
      throw error;
    }
  };

  const updateItemYear = async (item, newYear) => {
    try {
      // Use current date with the selected year
      const now = new Date();
      const newDate = new Date(newYear, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
      const formattedDate = newDate.toISOString().slice(0, 19).replace('T', ' ');

      await inventoryService.updateInventoryItem(item.Wig_Bar_Code, 'DateModified', formattedDate, item.source);

      // Update local state with new year and DateModified
      const updateItem = (inv) =>
        inv.Wig_Bar_Code === item.Wig_Bar_Code && inv.source === item.source
          ? { ...inv, DateModified: formattedDate, year: newYear }
          : inv;

      setAllInventory(prev => prev.map(updateItem));
      setFilteredInventory(prev => prev.map(updateItem));

      // Refresh counts
      fetchYearCounts();

      toast.success(`Year updated to ${newYear}`);
    } catch (error) {
      console.error('Error updating year:', error);
      toast.error('Failed to update year: ' + error.message);
    }
  };

  // Helper function to reset the add form
  const resetAddForm = () => {
    setNewInventoryItem({});
    setShowAddForm(false);
    setShowWigTypeOther(false);
    setWigTypeOther('');
    setShowTopSizeOther(false);
    setTopSizeOther('');
  };

  const addNewInventoryItem = async () => {
    try {
      const itemWithDefaults = {
        ...DEFAULT_WIG_VALUES,
        ...newInventoryItem //user inputs override defaults
      };

      await inventoryService.addInventoryItem(itemWithDefaults);

      // Show success toast notification
      toast.success(`Wig ${newInventoryItem.Wig_Bar_Code || ''} added successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Only clear the barcode field, keep other fields for bulk entry
      setNewInventoryItem(prev => ({
        ...prev,
        Wig_Bar_Code: ''
      }));

      // Keep the form open (don't call resetAddForm)
      fetchAllInventoryData();

      // Focus the barcode input for quick entry of next item
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Error adding inventory:', error);
      toast.error('Failed to add inventory item: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'available':
        return 'status-available';
      case 'sold':
        return 'status-sold';
      case 'pre-assembly':
        return 'status-pre-assembly';
      default:
        return '';
    }
  };


  // Combined function to fetch both status and year counts in one API call
  const fetchStatusCounts = async (updateYearCounts = false) => {
    try {
      const data = await inventoryService.getStats(activeYearTab);

      if (data.success) {
        const available = data.statusBreakdown.available || 0;
        const sold = data.statusBreakdown.sold || 0;
        const preAssembly = data.statusBreakdown['pre-assembly'] || 0;
        const other = Math.max(0, data.total - (available + sold + preAssembly));

        const counts = {
          all: data.total,
          available: available,
          sold: sold,
          'pre-assembly': preAssembly,
          other: other
        };

        // Set API counts (these are used for tabs)
        setApiCounts(counts);

        // Also set status counts (unless we're in search mode)
        if (!searchQuery.trim()) {
          setStatusCounts(counts);
        }

        // Update year counts if requested (on initial load or when needed)
        // Backend now always returns yearBreakdown - no extra API call needed
        if (updateYearCounts && data.yearBreakdown) {
          setYearCounts(data.yearBreakdown);
        }
      }
    } catch (err) {
      console.error('Error fetching status counts:', err);
    }
  };

  // Keep for backwards compatibility but now just calls fetchStatusCounts
  const fetchYearCounts = async () => {
    // Year counts now come from the same API call as status counts
    // Just trigger a refresh with updateYearCounts=true
    await fetchStatusCounts(true);
  };

  // Add this function for tab changes
  const handleStatusTabChange = (status) => {
    setActiveStatusTab(status);
    setCurrentPage(1); // Reset to first page when changing status
  };

  // Update handleFilterChange to only update pending filters
  const handlePendingFilterChange = (filterKey, value) => {
    setPendingFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  // New function to apply the pending filters
  const applyAdvancedFilters = () => {
    setAppliedFilters(pendingFilters);
    setCurrentPage(1);
  };


  
  const resetFilters = () => {
     const defaultFilters = {
    hairLength: [], // Change from 'all' to empty array for multiselect
    wigType: [],
    cutStyle: [],
    baseColor: [],
    inventoryType: [],
    netSize: [],
    hairType: [],
    netType: [],
    laceType: [],
    warehouse: [],
    topSize: []
  };
    
    setPendingFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setActiveStatusTab('all');
    setActiveYearTab('all');
    setSearchQuery('');
    setCurrentPage(1);
    
    // Reset to original data
    setFilteredInventory(allInventory);
    fetchStatusCounts();
    fetchYearCounts();
  };

  const updateCountsFromFiltered = (filtered) => {
    // Only update counts when searching, not for normal filtering
    if (searchQuery.trim()) {
      const available = filtered.filter(item => 
        (item.InvStatus || '').toLowerCase() === 'available'
      ).length;
      
      const sold = filtered.filter(item => 
        (item.InvStatus || '').toLowerCase() === 'sold'
      ).length;
      
      const preAssembly = filtered.filter(item => 
        (item.InvStatus || '').toLowerCase() === 'pre-assembly'
      ).length;
      
      const other = filtered.length - (available + sold + preAssembly);
      
      setStatusCounts({
        all: filtered.length,
        available: available,
        sold: sold,
        'pre-assembly': preAssembly,
        other: Math.max(0, other)
      });
    }
    // Don't update counts for non-search filtering - use API counts instead
  };

  // Add this useEffect to your component
useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.year-cell') && !event.target.closest('.added-by-cell')) {
      setShowDeleteIcon(null);
      setEditingYear(null);
    }
  };

  if (showDeleteIcon || editingYear) {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }
}, [showDeleteIcon, editingYear]);


  if (loading) {
    return (
      <div className="inventory-container">
        <div className="loading-state">
          <h2>Loading inventory data...</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inventory-container">
        <div className="error-state">
          <h2>Inventory Status</h2>
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={fetchAllInventoryData} className="retry-button">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1 className="inventory-title">Inventory Management</h1>
        
        {/* Search Bar */}
        <div className="search-container">
         
           <input
              type="text"
              placeholder="Search by barcode, order#, type, color, style..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} // Only update display value
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(searchQuery); // Trigger search on Enter
                }
              }}
              onBlur={() => {
                if (searchQuery.trim()) {
                  handleSearch(searchQuery); // Trigger search on blur (clicking outside)
                }
              }}
              className="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                className="clear-button"
                title="Clear search"
              >
                ✕
              </button>
            )}
          
          {searchLoading && (
            <div className="search-loading">Searching...</div>
          )}
        </div>

 
        {/* Year Tabs - 2024, 2025, 2026, Archive */}
        <div className="status-tabs">
          {[
            // { key: 'all', label: 'All Years', count: yearCounts.all },
            { key: '2026', label: '2026', count: yearCounts['2026'] || 0 },
            { key: '2025', label: '2025', count: yearCounts['2025'] || 0 },
            { key: '2024', label: '2024', count: yearCounts['2024'] || 0 },
            { key: 'archive', label: 'Archive (2020-2023)', count: yearCounts['archive'] || 0 }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => handleYearTabChange(tab.key)}
              className={`status-tab ${activeYearTab === tab.key ? 'active' : ''}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        {/* Status Tabs - use apiCounts instead of statusCounts */}
        <div className="status-tabs">
          {[
            { key: 'all', label: 'All Items', count: apiCounts.all },
            { key: 'available', label: 'Available', count: apiCounts.available },
            { key: 'pre-assembly', label: 'Manufacturing', count: apiCounts['pre-assembly'] },
            { key: 'sold', label: 'Sold', count: apiCounts.sold },
            { key: 'other', label: 'Other', count: apiCounts.other }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => handleStatusTabChange(tab.key)}
              className={`status-tab ${activeStatusTab === tab.key ? 'active' : ''}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
       {/* Controls */}
        <div className="inventory-controls">
          <button onClick={fetchAllInventoryData} className="refresh-button">
            🔄 Refresh Data
          </button>
          
          <button 
            onClick={() => setShowAddForm(!showAddForm)} 
            className="add-button"
          >
            ➕ Add New Item
          </button>

          <button
            onClick={() => setShowQuickSum(true)}
            className="quicksum-button"
            title="Generate quick summary breakdown"
          >
            📊 Quick Sum
          </button>

          <button
            onClick={() => setShowBulkWarehouseModal(true)}
            className="bulk-warehouse-button"
            title="Auto-complete multiple orders at once"
          >
            Bulk Auto-Complete
          </button>

{/* Advanced Filters Toggle */}
<button 
  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} 
  className="advanced-filters-toggle"
>
  {showAdvancedFilters ? '▼' : '▶'} Advanced Filters
</button>

{/* Advanced Filters Panel */}
{showAdvancedFilters && (
  <div className="advanced-filters-panel">
    <div className="filters-grid">

    <div className="filter-group">
        <label>Hair Length:</label>
        <MultiSelect
          options={WIG_OPTIONS.hairLengths}
          value={pendingFilters.hairLength}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, hairLength: value }))}
        />
      </div>
       <div className="filter-group">
        <label>Wig Type:</label>
        <MultiSelect
          options={WIG_OPTIONS.wigTypes}
          value={pendingFilters.wigType}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, wigType: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Cut Style:</label>
        <MultiSelect
          options={WIG_OPTIONS.cutStyles}
          value={pendingFilters.cutStyle}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, cutStyle: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Base Color:</label>
        <MultiSelect
          options={[...WIG_OPTIONS.baseColors, ...WIG_OPTIONS.aidelColors]}
          value={pendingFilters.baseColor}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, baseColor: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Inventory Type:</label>
        <MultiSelect
          options={WIG_OPTIONS.inventoryTypes}
          value={pendingFilters.inventoryType}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, inventoryType: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Net Size:</label>
        <MultiSelect
          options={WIG_OPTIONS.netSizes}
          value={pendingFilters.netSize}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, netSize: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Hair Type:</label>
        <MultiSelect
          options={WIG_OPTIONS.hairTypes}
          value={pendingFilters.hairType}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, hairType: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Net Type:</label>
        <MultiSelect
          options={WIG_OPTIONS.netTypes}
          value={pendingFilters.netType}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, netType: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Lace Type:</label>
        <MultiSelect
          options={WIG_OPTIONS.laceTypes}
          value={pendingFilters.laceType}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, laceType: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Top Size:</label>
        <MultiSelect
          options={WIG_OPTIONS.topSizes}
          value={pendingFilters.topSize}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, topSize: value }))}
        />
      </div>

      <div className="filter-group">
        <label>Warehouse:</label>
        <MultiSelect
          options={WIG_OPTIONS.warehouseTypes}
          value={pendingFilters.warehouse}
          onChange={(value) => setPendingFilters(prev => ({ ...prev, warehouse: value }))}
        />
      </div>
    </div>

    {/* Filter Action Buttons */}
    <div className="filter-actions">
      <button onClick={applyAdvancedFilters} className="apply-filters-btn">
        🔍 Apply Filters
      </button>
      <button onClick={resetFilters} className="reset-filters-btn">
        🔄 Clear All Filters
      </button>
      <div>
        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} items
      </div>
    </div>
  </div>
)}

          <div className="last-updated">
            Last updated: {new Date().toLocaleTimeString()}
          </div>
          <div className="page-size-selector">
              <label>
                Items per page:
                <select 
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>
            </div>
        </div>
        {/* Search Results Info */}
        {searchQuery && (
          <div className="search-results-info">
            Search results for "{searchQuery}": {filteredInventory.length} items found
          </div>
        )}
        

      
      </div>

      {/* Add New Item Form - Inline Panel */}
      {showAddForm && (
        <div className="add-item-panel">
          <h3>Add New Inventory Item</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Barcode *</label>
              <input
                ref={barcodeInputRef}
                type="text"
                placeholder="Enter barcode"
                value={newInventoryItem.Wig_Bar_Code || ''}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Wig_Bar_Code: e.target.value})}
              />
            </div>

            <div className="form-field">
              <label>Cut Style:</label>
              <select
                value={newInventoryItem.CutByStyle || 'DiNi'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, CutByStyle: e.target.value})}
              >
                {WIG_OPTIONS.cutStyles.map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Wig Type:</label>
              <select
                value={showWigTypeOther ? 'Other' : (newInventoryItem.Wig_Type || 'Standard Lace Top')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'Other') {
                    setShowWigTypeOther(true);
                    setNewInventoryItem({...newInventoryItem, Wig_Type: ''});
                  } else {
                    setShowWigTypeOther(false);
                    setWigTypeOther('');
                    setNewInventoryItem({...newInventoryItem, Wig_Type: value});
                  }
                }}
              >
                {WIG_OPTIONS.wigTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {showWigTypeOther && (
                <input
                  type="text"
                  placeholder="Specify wig type..."
                  value={wigTypeOther}
                  onChange={(e) => {
                    setWigTypeOther(e.target.value);
                    setNewInventoryItem({...newInventoryItem, Wig_Type: e.target.value});
                  }}
                  style={{ marginTop: '5px', width: '100%' }}
                />
              )}
            </div>

            <div className="form-field">
              <label>Base Color:</label>
              <select
                value={newInventoryItem.Base_Color || 'Dark'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Base_Color: e.target.value})}
              >
                {getColorOptions(newInventoryItem.Wig_Type).map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Top Size:</label>
              <select
                value={showTopSizeOther ? 'Other' : (newInventoryItem.Top_Size || '6"x 4" 100%')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'Other') {
                    setShowTopSizeOther(true);
                    setNewInventoryItem({...newInventoryItem, Top_Size: ''});
                  } else {
                    setShowTopSizeOther(false);
                    setTopSizeOther('');
                    setNewInventoryItem({...newInventoryItem, Top_Size: value});
                  }
                }}
              >
                {WIG_OPTIONS.topSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {showTopSizeOther && (
                <input
                  type="text"
                  placeholder="Specify top size..."
                  value={topSizeOther}
                  onChange={(e) => {
                    setTopSizeOther(e.target.value);
                    setNewInventoryItem({...newInventoryItem, Top_Size: e.target.value});
                  }}
                  style={{ marginTop: '5px', width: '100%' }}
                />
              )}
            </div>

            <div className="form-field">
              <label>Length:</label>
              <select
                value={newInventoryItem.Wig_Length || 'Short'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Wig_Length: e.target.value})}
              >
                {WIG_OPTIONS.hairLengths.map(length => (
                  <option key={length} value={length}>{length}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Net Size:</label>
              <select
                value={newInventoryItem.Net_Size || 'M'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Net_Size: e.target.value})}
              >
                {WIG_OPTIONS.netSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Hair Type:</label>
              <select
                value={newInventoryItem.Hair_Type || 'Straight'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Hair_Type: e.target.value})}
              >
                {getHairTypeOptions(newInventoryItem.Wig_Type).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Net Type:</label>
              <select
                value={newInventoryItem.Net_Type || 'Deep Net'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Net_Type: e.target.value})}
              >
                {WIG_OPTIONS.netTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Lace Type:</label>
              <select
                value={newInventoryItem.Lace_Type || 'No Cut'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Lace_Type: e.target.value})}
              >
                {WIG_OPTIONS.laceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Warehouse:</label>
              <select
                value={newInventoryItem.Warehouse || 'Pomona'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Warehouse: e.target.value})}
              >
                {WIG_OPTIONS.warehouseTypes.map(warehouse => (
                  <option key={warehouse} value={warehouse}>{warehouse}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Stock Type:</label>
              <select
                value={newInventoryItem.InventoryType || 'Stock'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, InventoryType: e.target.value})}
              >
                {WIG_OPTIONS.inventoryTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Status:</label>
              <select
                value={newInventoryItem.InvStatus || 'Available'}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, InvStatus: e.target.value})}
              >
                {WIG_OPTIONS.invStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Comments:</label>
              <input
                type="text"
                placeholder="Add comments..."
                value={newInventoryItem.Custom_Comments || ''}
                onChange={(e) => setNewInventoryItem({...newInventoryItem, Custom_Comments: e.target.value})}
              />
            </div>
          </div>

          <div className="form-actions">
            <button onClick={addNewInventoryItem} className="save-item-btn">
              💾 Save Item
            </button>
            <button onClick={resetAddForm} className="cancel-item-btn">
              ✕ Cancel
            </button>
          </div>
        </div>
      )}
      {/* Inventory Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Order Link</th>
                <th>Barcode</th>
                {/* <th>Serial Code</th> */}
                <th>Status</th>
                <th>Comments</th>
                <th>Cut Style</th>
                <th>Wig Type</th>
                <th>Base Color</th>
                <th>Length</th>
                <th>Net Size</th>
                <th>Hair Type</th>
                <th>Net Type</th>
                <th>Lace Type</th>
                <th>Top Size</th>
                <th>Warehouse</th>
                <th>Inventory Type</th>
                <th>Date Added</th>
                <th>Client ID</th>
                <th>Old Barcode</th>
                <th>Added By</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item, index) => (
                <tr key={`${item.source}-${item.Wig_Bar_Code}-${index}`}>
<td>
  <div
    className="year-cell"
    onClick={(e) => {
      e.stopPropagation();
      // Single click to show dropdown
      if (editingYear === `${item.source}-${item.Wig_Bar_Code}-${index}`) {
        setEditingYear(null);
      } else {
        setEditingYear(`${item.source}-${item.Wig_Bar_Code}-${index}`);
      }
    }}
  >
    {editingYear === `${item.source}-${item.Wig_Bar_Code}-${index}` ? (
      <select
        className="year-dropdown"
        value={item.year || '2025'}
        onChange={(e) => {
          updateItemYear(item, e.target.value);
          setEditingYear(null);
        }}
        onClick={(e) => e.stopPropagation()}
        autoFocus
      >
        <option value="2026">2026</option>
        <option value="2025">2025</option>
        <option value="2024">2024</option>
        <option value="2023">2023</option>
      </select>
    ) : (
      <span className={`source-badge year-${item.year || 'unknown'}`}>
        {item.year || 'Unknown'}
      </span>
    )}
  </div>
</td>
                  <td>
                    {item.OrderIDLink ? (
                      <a 
                        href={`/work-order-profile/${item.OrderIDLink}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="order-link"
                      >
                        #{item.OrderIDLink}
                      </a>
                    ) : (
                      <span className="no-link">No Link</span>
                    )}
                  </td>
                  <td>
                    <EditableCell
                      value={item.Wig_Bar_Code}
                      onSave={(value) => updateBarcode(item, value)}
                      placeholder="Enter barcode"
                      className="barcode"
                    />
                  </td>
                  {/* <td>
                    <EditableCell
                      value={item.Wig_Serial_Code}
                      onSave={(value) => updateInventoryItem(item, 'Wig_Serial_Code', value)}
                      placeholder="Enter serial code"
                    />
                  </td> */}
                  <td>
  <EditableCell
    value={item.InvStatus}
    onSave={(value) => updateInventoryItem(item, 'InvStatus', value)}
    type="select"
    options={WIG_OPTIONS.invStatuses.map(status => ({
      value: status,
      label: status
    }))}
    className={`status-badge ${getStatusClass(item.InvStatus)}`}
  />
</td>

<td>
  <EditableCell
    value={item.Custom_Comments || ''}
    onSave={(value) => updateInventoryItem(item, 'Custom_Comments', value)}
    placeholder="Add comments..."
    multiline={true}  // This enables textarea mode
    className="comments-cell"
  />
</td>
                  <td>
                    <EditableCell
                      value={item.CutByStyle}
                      onSave={(value) => updateInventoryItem(item, 'CutByStyle', value)}
                      type="select"
                      options={WIG_OPTIONS.cutStyles.map(style => ({
                        value: style,
                        label: style
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Wig_Type}
                      onSave={(value) => updateInventoryItem(item, 'Wig_Type', value)}
                      type="select"
                      options={WIG_OPTIONS.wigTypes.map(type => ({
                        value: type,
                        label: type
                      }))}
                      allowOther={true}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Base_Color}
                      onSave={(value) => updateInventoryItem(item, 'Base_Color', value)}
                      type="select"
                      options={getColorOptions(item.Wig_Type).map(color => ({
                        value: color,
                        label: color
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Wig_Length}
                      onSave={(value) => updateInventoryItem(item, 'Wig_Length', value)}
                      type="select"
                      options={WIG_OPTIONS.hairLengths.map(length => ({
                        value: length,
                        label: length
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Net_Size}
                      onSave={(value) => updateInventoryItem(item, 'Net_Size', value)}
                      type="select"
                      options={WIG_OPTIONS.netSizes.map(size => ({
                        value: size,
                        label: size
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Hair_Type}
                      onSave={(value) => updateInventoryItem(item, 'Hair_Type', value)}
                      type="select"
                      options={getHairTypeOptions(item.Wig_Type).map(type => ({
                        value: type,
                        label: type
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Net_Type}
                      onSave={(value) => updateInventoryItem(item, 'Net_Type', value)}
                      type="select"
                      options={WIG_OPTIONS.netTypes.map(type => ({
                        value: type,
                        label: type
                      }))}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={item.Lace_Type}
                      onSave={(value) => updateInventoryItem(item, 'Lace_Type', value)}
                      type="select"
                      options={WIG_OPTIONS.laceTypes.map(type => ({
                        value: type,
                        label: type
                      }))}
                    />
                  </td>
                   <td>
                    <EditableCell
                      value={item.Top_Size}
                      onSave={(value) => updateInventoryItem(item, 'Top_Size', value)}
                      type="select"
                      options={WIG_OPTIONS.topSizes.map(size => ({
                        value: size,
                        label: size
                      }))}
                      allowOther={true}
                    />
                  </td>
                    <td>
                    {item.WarehouseEditable ? (
                      <EditableCell
                        value={item.Warehouse}
                        onSave={(value) => updateInventoryItem(item, 'Warehouse', value)}
                        type="select"
                        options={WIG_OPTIONS.warehouseTypes.map(type => ({
                          value: type,
                          label: type
                        }))}
                      />
                    ) : (
                      <span className="warehouse-from-room" title="Location from current room assignment">
                        {item.Warehouse}
                      </span>
                    )}
                  </td>
                  <td>
                    <EditableCell
                      value={item.InventoryType}
                      onSave={(value) => updateInventoryItem(item, 'InventoryType', value)}
                      type="select"
                      options={WIG_OPTIONS.inventoryTypes.map(type => ({
                        value: type,
                        label: type
                      }))}
                    />
                  </td>
                
                <td>{formatDate(item.DateAdded)}</td>
                 <td>
                    {item.ClientID ? (
                      <a
                        href={`/customers/${item.ClientID}`}
                        className="client-link"
                      >
                        #{item.ClientID}
                      </a>
                    ) : (
                      <span className="no-link">-</span>
                    )}
                  </td>
                  <td>{item.OldBarcode || '-'}</td>
                  <td>
                    <div
                      className="added-by-cell"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteIcon(`${item.source}-${item.Wig_Bar_Code}-${index}`);
                      }}
                    >
                      {showDeleteIcon === `${item.source}-${item.Wig_Bar_Code}-${index}` ? (
                        <button
                          className="delete-icon-transformed"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteItem(item);
                          }}
                          title="Delete this item permanently"
                        >
                          🗑️
                        </button>
                      ) : (
                        <span>{item.AddedBy || 'N/A'}</span>
                      )}
                    </div>
                  </td>
          
                </tr>
              ))}
            </tbody>
          </table>

          
        </div>
                {/* Pagination Controls - Add this section */}
        {showPagination && !searchQuery && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} items
            </div>
            
            <div className="pagination-buttons">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                First
              </button>
              
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              
              <span className="page-indicator">
                Page {currentPage} of {Math.ceil(totalRecords / pageSize)}
              </span>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
                className="pagination-btn"
              >
                Next
              </button>
              
              <button
                onClick={() => setCurrentPage(Math.ceil(totalRecords / pageSize))}
                disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
                className="pagination-btn"
              >
                Last
              </button>
            </div>
            
            <div className="page-size-selector">
              <label>
                Items per page:
                <select 
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>

      {filteredInventory.length === 0 && !loading && (
        <div className="no-results">
          <h3>No items found</h3>
          <p>Try adjusting your search criteria or status filter.</p>
        </div>
      )}

      <QuickSumModal
        isOpen={showQuickSum}
        onClose={() => setShowQuickSum(false)}
        appliedFilters={appliedFilters}
        activeStatusTab={activeStatusTab}
        activeYearTab={activeYearTab}
      />

      <BulkWarehouseModal
        isOpen={showBulkWarehouseModal}
        onClose={() => setShowBulkWarehouseModal(false)}
        onComplete={() => {
          setShowBulkWarehouseModal(false);
          fetchAllInventoryData();
          fetchStatusCounts(true);
        }}
      />

    </div>
  );
};

export default InventoryPage;